import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { exhibitorSet }  from '../../store/exhibitorSlice';
import { useNavigate, NavLink, useParams } from "react-router-dom";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faHouse, faCopyright, faUser } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan, faRotate, faShield } from '@fortawesome/free-solid-svg-icons';
import { del, get } from '../../utils/api';
import useApiGet from '../../hooks/useApiGet';
import LogoForm from '../common/LogoForm';
import Tooltip from '../common/Tooltip';
import Form from '../common/Form';
import ExhibitorViewFiles from './ExhibitorViewFiles';
import ExhibitorViewStickers from './ExhibitorViewStickers';
import { getCountryName } from '../../utils/countries';
import './ExhibitorView.css';
import ExhibitorViewMainCategories from './ExhibitorViewMainCategories';
import ActivityList from './activity/ActivityList';


const ExhibitorView = () => {
    const { id, action } = useParams();
	const user = useSelector(state => state.auth.user);
	const method = '/exhibitors/' + id;
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
    const lang = i18n.language;
	const [ data, error, isLoading ] = useApiGet({ method });
    const [ main, setMain ] = useState([]);
    const [ addon, setAddon ] = useState([]);
    const [ admin, setAdmin ] = useState([]);
	const [ deleteError, setDeleteError ] = useState('');
	const [ deleteSuccess, setDeleteSuccess ] = useState('');
	const [ deleteProcess, setDeleteProcess ] = useState(false);
	const [ catCount, setCatCount ] = useState({ main: 0, addon: 0, admin: 0 });
	const [ catEvent, setCatEvent ] = useState([]);

	// Обработка данных с сервера
	useEffect(() => {
		if(!data) return;
		// Отсортируем стикеры
		data.exhibitor.stickers.sort((a, b) => a.priority - b.priority);
		// Запишем экспонента в стор
		dispatch(exhibitorSet(data.exhibitor));
		// Разбор категорий по типам
        const main  = [];
        const addon = [];
        const admin = [];
        data.exhibitor.categories.forEach(item => {
            switch(item.type) {
                case 'admin':
                    admin.push(item);
                    break;
                case 'addon':
                    addon.push(item);
                    break;
                default:
                    main.push(item);
                    break;
            }
        });
        setMain(main);
        setAddon(addon);
        setAdmin(admin);
	},[ data, dispatch ]);

	// Запрашиваем информацию о категориях мероприятия для того,
	// чтобы определить надо или нет показывать дополнительные категории
	// и для отображения папок основных категорий.
	useEffect(() => {
		const fetchCategories = async (event) => {
			const result = await get('/categories/', { event });
			if(result.error) return;
			const count = { main: 0, addon: 0, admin: 0 };
			result.data.categories.forEach(cat => {
				if(count[cat.type]) count[cat.type] ++; else count[cat.type] = 1;
			});
			setCatCount(count);
			setCatEvent(result.data.categories);
		};

		setCatCount({ main: 0, addon: 0, admin: 0 });
		if(data?.exhibitor?.event?._id) fetchCategories(data.exhibitor.event._id);
	}, [ data ]);

	// Удаляем экспонента
	const onDelete = async () => {
		setDeleteError('');
		setDeleteSuccess('');
		setDeleteProcess(true);

		if(!window.confirm(t('exhibitorview.delete_confirm'))) {
			setDeleteProcess(false);
			return;
		}

		const result = await del(method);

        if(result.error) {
            setDeleteError(result.error);
        } else {
            setDeleteSuccess('exhibitorview.delete_success');
        }
		setDeleteProcess(false);
	};

	// Переход в каталог
	const openCatalog = () => {
		const prot = window.location.protocol;
		const host = window.location.hostname;
		let url = prot + '//' + host + '/catalog/exhibitor/' + id;	
		if(data?.exhibitor?.event?.catalog_url[lang]) {
			url = `${data.exhibitor.event.catalog_url[lang]}?type=exhibitor&id=${id}`;
		}
		//console.log(url);
		window.open(url);
	};

	return (
		<div>
	    	<h1>{data ? data.exhibitor.name[lang] : t('exhibitorview.title')}</h1>
            {error && <p className='error'>{t(error)}</p>}
			{deleteError && <p className='error'>{t(deleteError)}</p>}
			{deleteSuccess && <p className='success'>{t(deleteSuccess)}</p>}
            {action === 'created' && <p className='success'>{t('exhibitorview.created')}</p>}
            {action === 'updated' && <p className='success'>{t('exhibitorview.updated')}</p>}
			{isLoading && <p>{t('common.loading')}</p>}
            {data &&
			<div>
				{t('logo.title')}
				<Tooltip>
					{t('logo.type')}<br/>
					{t('logo.size')}<br/>
					{t('logo.dimension')}
				</Tooltip>
				<div className='Exhibitor'>
					<div className='Exhibitor-logo'>
						<LogoForm target={data}/>
					</div>
					<div className='Exhibitor-text'>
						<ul className='fa-ul'>
							<li>{/* Мероприятие */}
								<Icon className='fa-li' icon={faLocationDot} style={{color:'var(--main-color)'}}/>&nbsp;
								{(user.role === 'exhibitor' || user.role === 'agent')
								? <NavLink to={'/cabinet/exhibitorlistbyevent/' + data.exhibitor.event._id}>
									{data.exhibitor.event.name[lang]}
								</NavLink>
								: <NavLink to={'/cabinet/eventview/' + data.exhibitor.event._id}>
									{data.exhibitor.event.name[lang]}
								</NavLink>}
							</li>
							<li>{/* Стенд */}
								<Icon className='fa-li' icon={faHouse} style={{color:'var(--main-color)'}}/>&nbsp;
								{data.exhibitor.stand ? data.exhibitor.stand : t('exhibitorview.nostand')}
							</li>
							<li>{/* Бренды */}
								<Icon className='fa-li' icon={faCopyright} style={{color:'var(--main-color)'}}/>&nbsp;
								{data.exhibitor.brand ? data.exhibitor.brand : t('exhibitorview.nobrand')}
							</li>
							<li>{/* Название для диплома*/}
								<Icon className='fa-li' icon={faShield} style={{color:'var(--main-color)'}}/>&nbsp;
								{data.exhibitor.diplom_name ? data.exhibitor.diplom_name : t('exhibitorview.nodiplomname')}
							</li>
							<li>{/* Администратор */}
								<Icon className='fa-li' icon={faUser} style={{color:'var(--main-color)'}}/>&nbsp;
								{user.role === 'exhibitor' ?
								<span>{data.exhibitor.admin.name}</span> :
								<NavLink to={'/cabinet/userview/' + data.exhibitor.admin._id}>
									{data.exhibitor.admin.name}
								</NavLink>}
							</li>
						</ul>
						<h2>
							<span onClick={() => navigate('/cabinet/exhibitoreditmain')}>{t('common.edit')}</span>
						</h2>
					</div>

					{/* Кнопка удаления */}
					{user.role !== 'exhibitor' &&
					<React.Fragment>
						{deleteProcess ?
						<div
							className='Exhibitor-menu'
							style={{color:'var(--remove-color)'}}
						>
							<Icon icon={faRotate} size='xl' className='fa-spin'/>
						</div> :
						<div
							className='Exhibitor-menu'
							onClick={() => onDelete()}
							style={{color:'var(--remove-color)'}}
						>
							<Icon icon={faTrashCan} size='xl'/>
						</div>}
					</React.Fragment>}
				</div>

				{/* Контакты */}
				<h2>
					{t('exhibitorview.contacts')}
					<span onClick={() => navigate('/cabinet/exhibitoreditcontacts')}>{t('common.edit')}</span>
				</h2>
				{!data.exhibitor.contacts ? t('exhibitorview.nocontacts') :
				<div>
					{data.exhibitor.contacts.country &&
						<div>{getCountryName(data.exhibitor.contacts.country, lang)}</div>
					}
					{data.exhibitor.contacts.city[lang] && <div>{data.exhibitor.contacts.city[lang]}</div>}
					{data.exhibitor.contacts.phone && <div>{data.exhibitor.contacts.phone}</div>}
					{data.exhibitor.contacts.email && <div>{data.exhibitor.contacts.email}</div>}
					{data.exhibitor.contacts.www && <div>{data.exhibitor.contacts.www}</div>}
				</div>}

				{/* Ссылки */}
				<h2>
					{t('exhibitorview.links')}
					<span onClick={() => navigate('/cabinet/exhibitoreditlinks')}>{t('common.edit')}</span>
				</h2>
				{!data.exhibitor.links ? t('exhibitorview.nolinks') :
				<div>
					{data.exhibitor.links.youtube && <div>{data.exhibitor.links.youtube}</div>}
					{data.exhibitor.links.rutube && <div>{data.exhibitor.links.rutube}</div>}
					{data.exhibitor.links.vk && <div>{data.exhibitor.links.vk}</div>}
					{data.exhibitor.links.telegram && <div>{data.exhibitor.links.telegram}</div>}
					{data.exhibitor.links.yazen && <div>{data.exhibitor.links.yazen}</div>}
				</div>}

				{/* Краткое описание */}
				<h2>
					{t('exhibitorview.brief')}
					<span onClick={() => navigate('/cabinet/exhibitoredittexts')}>{t('common.edit')}</span>
				</h2>
				<div>{data.exhibitor.brief[lang] ? data.exhibitor.brief[lang] : t('exhibitorview.nobrief')}</div>

				{/* Список категорий */}
				<h2>
					{t('exhibitorview.categories')}
					<span onClick={() => navigate('/cabinet/exhibitoreditcategories')}>{t('common.edit')}</span>
				</h2>

				{/* Основные категории */}
				<ExhibitorViewMainCategories categoriesChosen={main} categoriesAll={catEvent}/>

				{/* Не показываем агенту и участнику блок если дополнительные категории в мероприятии не определены */}
				{!((user.role === 'exhibitor' || user.role === 'agent') && catCount.addon < 1) && <>
					<h3>{t('eventview.cataddon')}</h3>
					{addon.length > 0 ?
						<ul>
						{addon.map(item => (
							<li key={item._id}>{item.name[lang]}</li>	
						))}
						</ul> : <div>{t('exhibitorview.nocategories')}</div>
					}
				</>}
				{/* Не показываем агенту и участнику блок если админские категории не назначены */}
				{!((user.role === 'exhibitor' || user.role === 'agent') && admin.length < 1) && <>
					<h3>{t('eventview.catadmin')}</h3>
					{admin.length > 0 ?
						<ul>
						{admin.map(item => (
							<li key={item._id}>{item.name[lang]}</li>	
						))}
						</ul> : <div>{t('exhibitorview.nocategories')}</div>
					}
				</>}

				{/* Список стикеров */}
				<ExhibitorViewStickers exhibitor={data.exhibitor}/>

				{/* Полное описание */}
				<h2>
					{t('exhibitorview.about')}
					<span onClick={() => navigate('/cabinet/exhibitoredittexts')}>{t('common.edit')}</span>
				</h2>
				<div>{data.exhibitor.about[lang] ? data.exhibitor.about[lang] : t('exhibitorview.noabout')}</div>

				{/* Зачем приходить на стенд */}
				<h2>
					{t('exhibitorview.descr')}
					<span onClick={() => navigate('/cabinet/exhibitoredittexts')}>{t('common.edit')}</span>
				</h2>
				<div>{data.exhibitor.descr[lang] ? data.exhibitor.descr[lang] : t('exhibitorview.nodescr')}</div>

				{/* Список файлов */}
				{data.exhibitor.event.is_files_available &&
				<ExhibitorViewFiles exhibitor={data.exhibitor}/>}

				{/* Список активностей */}
				{(Array.isArray(data.exhibitor.event.dates) && data.exhibitor.event.dates.length > 0) &&
				<ActivityList exhibitor={data.exhibitor}/>}

				{/* Переход в каталог */}
				<div style={{borderTop:'1px solid var(--border-color)', marginTop:'2rem'}}>
					<Form info='exhibitorview.catalog_info' wide={true}>
						<input
							type='submit'
							value={t('exhibitorview.catalog_submit')}
							onClick={() => openCatalog()}
						/>
					</Form>
				</div>

			</div>}
		</div>
	);
};

export default ExhibitorView;
